import { DEPLOY_SECRET_NAME } from './runner-template.js';
/**
 * The Azure deploy-access script (IM-45): the deploy app registration, and the
 * one client secret whose expiry is the grant (IM-43 Q1).
 *
 * ## Why a script the customer runs, and not a template
 *
 * ARM cannot create an Entra application, so something has to run against
 * Microsoft Graph. The two candidates were a deployment script inside the
 * runner template, and this.
 *
 * - **A deployment script** runs `az` in a container in the customer's
 *   subscription, as a managed identity. That identity would need
 *   `Application.ReadWrite.All` on Graph, a tenant-wide right to create and
 *   change *any* application, held standing, by an identity a template made.
 *   It is worse than the thing it creates. It also runs the secret through a
 *   container, a storage account and a deployment's history before it reaches
 *   Key Vault, and a security team reviewing it has to read the template, the
 *   script inside it and the Graph grant together.
 * - **This script** runs as whoever the customer chooses — an owner of the app
 *   or an Application Administrator — on their own machine, once. Nothing is
 *   left holding Graph rights afterwards. The secret goes from Entra to Key
 *   Vault through one shell variable and a mode-600 temporary file, and is
 *   never printed. It is about a hundred lines with a comment per step, which
 *   is something a reviewer can read in one sitting.
 *
 * Microsoft's Graph Bicep extension was not an option: it cannot create a
 * secret, which is the part that matters.
 *
 * ## The secret is written through ARM, not the Key Vault data plane
 *
 * `az keyvault secret set` would need the person running this to hold a Key
 * Vault data role, which nobody should hold standing just to renew a grant.
 * Writing the secret as an ARM resource needs only `secrets/write` on the
 * vault, which an owner of the runner's group already has, and sets the
 * secret's `exp` to the grant date in the same call. That `exp` is what the
 * dispatcher and the onboarding screen read; Entra's `endDateTime` is what
 * actually closes access, and the two are set from one value here.
 *
 * ## Exactly one credential
 *
 * Qrvey cannot see how many credentials the app holds: Lighthouse reaches ARM,
 * not Graph. A second, longer-lived secret would silently defeat the time-box.
 * So `grant` removes every other credential once the new one is stored, and
 * `verify` fails unless exactly one is unexpired.
 */
/** Bumped whenever the script changes. Written into the secret's content type. */
export const AZURE_DEPLOY_ACCESS_VERSION = '1';
/** The content type the script gives the secret, which carries its version. */
export const DEPLOY_SECRET_CONTENT_TYPE = `qrvey-deploy-access/v${AZURE_DEPLOY_ACCESS_VERSION}`;
export const DEPLOY_APP_NAME = 'qrvey-deploy';
export const buildDeployAccessScript = (input = {}) => {
    const defaultExpiry = input.defaultExpiresAt === undefined ? '' : `${input.defaultExpiresAt}Z`;
    return `#!/usr/bin/env bash
# Qrvey deploy access, v${AZURE_DEPLOY_ACCESS_VERSION}. Generated by Qrvey Instance Manager.
#
# Read this before running it. It runs as you, in your tenant, and sends
# nothing to Qrvey.
#
#   create   Creates the app registration Qrvey's runner signs in as
#            (${DEPLOY_APP_NAME}), with no API permissions and no redirect
#            URIs, and its service principal. Prints the two ids the runner and
#            instance templates ask for. Safe to run again.
#   grant    Mints the app's one client secret, ending at the date you give,
#            and stores it in the runner's Key Vault. Then removes every other
#            credential the app holds, so that date is the only one. Qrvey's
#            deploy access ends then: Microsoft Entra issues no token after it,
#            and a token issued just before lasts at most about an hour more.
#   verify   Lists the app's credentials. Fails unless exactly one is unexpired.
#   revoke   Removes every credential now. Runs stop at their next token
#            renewal, within about an hour.
#
# The secret goes from Entra to your Key Vault on this machine and is never
# printed. Needs: az, signed in to the tenant; an owner of the app (or
# Application Administrator); and secrets/write on the runner's Key Vault,
# which the runner group's owners have.
#
# Usage:
#   ./qrvey-deploy-access.sh create
#   QRVEY_RUNNER_GROUP=<runner resource group> ./qrvey-deploy-access.sh grant 2026-12-31T00:00:00Z
#   ./qrvey-deploy-access.sh verify
set -euo pipefail

VERSION=${AZURE_DEPLOY_ACCESS_VERSION}
APP_NAME="\${QRVEY_DEPLOY_APP_NAME:-${DEPLOY_APP_NAME}}"
SECRET_NAME=${DEPLOY_SECRET_NAME}
DEFAULT_EXPIRY="${defaultExpiry}"
BODY=

die() {
  echo "qrvey-deploy-access: $*" >&2
  exit 1
}

now_utc() { date -u +%Y-%m-%dT%H:%M:%SZ; }

# Seconds since 1970 for YYYY-MM-DDTHH:MM:SSZ. Arithmetic rather than date(1),
# whose flags differ between Linux and macOS.
epoch_of() {
  local y=$((10#\${1:0:4})) m=$((10#\${1:5:2})) d=$((10#\${1:8:2}))
  local hh=$((10#\${1:11:2})) mm=$((10#\${1:14:2})) ss=$((10#\${1:17:2}))
  if [ "$m" -le 2 ]; then y=$((y - 1)); fi
  local era=$((y / 400)) yoe=$((y % 400))
  local doy=$(((153 * ((m + 9) % 12) + 2) / 5 + d - 1))
  local doe=$((yoe * 365 + yoe / 4 - yoe / 100 + doy))
  echo $(((era * 146097 + doe - 719468) * 86400 + hh * 3600 + mm * 60 + ss))
}

# The app's client id, or nothing. Two apps by one name is a mistake to fix,
# not one to guess between.
app_id() {
  local ids count
  ids=$(az ad app list --display-name "$APP_NAME" --query '[].appId' -o tsv)
  count=$(printf '%s\\n' "$ids" | grep -c . || true)
  if [ "$count" -gt 1 ]; then
    die "$count app registrations are named $APP_NAME. Delete the extras, or set QRVEY_DEPLOY_APP_NAME."
  fi
  printf '%s' "$ids"
}

cmd_create() {
  local app sp me tenant
  app=$(app_id)
  if [ -z "$app" ]; then
    app=$(az ad app create --display-name "$APP_NAME" --sign-in-audience AzureADMyOrg --query appId -o tsv)
    echo "Created the app registration $APP_NAME ($app)." >&2
  else
    echo "The app registration $APP_NAME already exists ($app)." >&2
  fi
  sp=$(az ad sp list --filter "appId eq '$app'" --query '[0].id' -o tsv)
  if [ -z "$sp" ]; then
    sp=$(az ad sp create --id "$app" --query id -o tsv)
  fi
  # You, as an owner, so you can renew the grant without a directory role.
  me=$(az ad signed-in-user show --query id -o tsv 2>/dev/null || true)
  if [ -n "$me" ]; then
    az ad app owner add --id "$app" --owner-object-id "$me" >/dev/null 2>&1 || true
  fi
  tenant=$(az account show --query tenantId -o tsv)
  echo "deployAppClientId=$app"
  echo "deployPrincipalId=$sp"
  echo "tenantId=$tenant"
}

cmd_grant() {
  local expiry=\${1:-$DEFAULT_EXPIRY} group=\${QRVEY_RUNNER_GROUP:-} app vaults count vault old secret key
  [ -n "$expiry" ] || die "Name the date deploy access should end, in UTC: grant YYYY-MM-DDTHH:MM:SSZ"
  case "$expiry" in
    [0-9][0-9][0-9][0-9]-[01][0-9]-[0-3][0-9]T[0-2][0-9]:[0-5][0-9]:[0-5][0-9]Z) ;;
    *) die "$expiry is not a UTC time as YYYY-MM-DDTHH:MM:SSZ" ;;
  esac
  [ "$expiry" \\> "$(now_utc)" ] || die "$expiry is not in the future"
  [ -n "$group" ] || die "Set QRVEY_RUNNER_GROUP to the runner's resource group."

  app=$(app_id)
  [ -n "$app" ] || die "There is no app registration named $APP_NAME. Run create first."
  vaults=$(az keyvault list --resource-group "$group" --query "[?tags.\\"qrvey-artifact\\"=='runner'].id" -o tsv)
  count=$(printf '%s\\n' "$vaults" | grep -c . || true)
  [ "$count" -eq 1 ] || die "Expected the runner's one Key Vault in $group, found $count."
  vault=$vaults
  old=$(az ad app credential list --id "$app" --query '[].keyId' -o tsv)

  BODY=$(mktemp)
  chmod 600 "$BODY"
  trap 'rm -f "$BODY"' EXIT

  # 1. Mint the new secret beside the old ones, so a run in flight keeps working.
  secret=$(az ad app credential reset --id "$app" --append --display-name "qrvey-grant-$expiry" --end-date "$expiry" --query password -o tsv)
  case "$secret" in
    '' | *[!A-Za-z0-9._~-]*)
      die "Entra returned a secret this script cannot store safely, and nothing was written. Run verify, then revoke the new credential."
      ;;
  esac

  # 2. Store it, with its expiry, where only the runner identity can read it.
  printf '{"properties":{"value":"%s","contentType":"qrvey-deploy-access/v%s","attributes":{"exp":%s}}}' \\
    "$secret" "$VERSION" "$(epoch_of "$expiry")" >"$BODY"
  secret=
  az rest --method put --url "https://management.azure.com\${vault}/secrets/\${SECRET_NAME}?api-version=2023-07-01" \\
    --body "@$BODY" --output none
  rm -f "$BODY"

  # 3. Only now remove the old ones, so the new date is the only date.
  for key in $old; do
    az ad app credential delete --id "$app" --key-id "$key"
  done
  echo "Deploy access now ends at $expiry." >&2
  cmd_verify
}

cmd_verify() {
  local app now rows live=0 key end name state
  app=$(app_id)
  [ -n "$app" ] || die "There is no app registration named $APP_NAME."
  now=$(now_utc)
  rows=$(
    az ad app credential list --id "$app" --query '[].[keyId, endDateTime, displayName]' -o tsv
    az ad app credential list --id "$app" --cert --query '[].[keyId, endDateTime, displayName]' -o tsv
  )
  echo "Credentials on $APP_NAME ($app):"
  while IFS=$'\\t' read -r key end name; do
    [ -n "$key" ] || continue
    if [ "\${end:0:19}Z" \\> "$now" ]; then
      state=current
      live=$((live + 1))
    else
      state=expired
    fi
    printf '  %s  ends %s  %s  %s\\n' "$key" "$end" "$state" "$name"
  done <<EOF
$rows
EOF
  [ "$live" -eq 1 ] || die "Expected exactly one unexpired credential, found $live. Run grant again, or revoke."
}

cmd_revoke() {
  local app key
  app=$(app_id)
  [ -n "$app" ] || die "There is no app registration named $APP_NAME."
  for key in $(az ad app credential list --id "$app" --query '[].keyId' -o tsv); do
    az ad app credential delete --id "$app" --key-id "$key"
  done
  for key in $(az ad app credential list --id "$app" --cert --query '[].keyId' -o tsv); do
    az ad app credential delete --id "$app" --key-id "$key"
  done
  echo "Every credential on $APP_NAME is removed." >&2
}

case "\${1:-}" in
  create) cmd_create ;;
  grant)
    shift
    cmd_grant "$@"
    ;;
  verify) cmd_verify ;;
  revoke) cmd_revoke ;;
  *)
    sed -n '2,30p' "$0" >&2
    exit 2
    ;;
esac
`;
};
//# sourceMappingURL=deploy-access.js.map